// src/lib/friendsData.js
// Resilient loader for the profile Friends section.
//
// Split out of MePage's FriendsSection so the merge logic that keeps the
// friends list from blanking can be unit-tested without a DOM/component
// harness.
//
// Bug this guards against (F-040): FriendsSection.refresh() loaded
// get_friends + get_pending_friend_requests + suggest_friends with a single
// Promise.all inside one swallowing `try/catch`. Promise.all is
// all-or-nothing, so when ONE sibling rejected — most plausibly
// suggest_friends timing out (a round_scores self-join whose cost scales
// with round history; see CLAUDE.md "Netlify functions time out at 10s") —
// the whole call rejected, the empty catch swallowed it, and NONE of the
// three states updated. The user's real accepted friends silently vanished
// and the section rendered "No friends yet".
//
// Promise.allSettled isolates each query: get_friends drives `friends` from
// its OWN result regardless of the siblings; a failing sibling leaves only
// its own slice empty. The `*Failed` flags let the caller keep prior state
// (rather than blank it) on a transient failure and surface a non-blocking
// hint when suggestions could not load.
import { db as defaultDb } from './db'

export async function loadFriendsData(playerId, dbFn = defaultDb) {
  const [friendsRes, pendingRes, suggestRes, sentRes] = await Promise.allSettled([
    dbFn('get_friends', { player_id: playerId }),
    dbFn('get_pending_friend_requests', { player_id: playerId }),
    dbFn('suggest_friends', { player_id: playerId }),
    dbFn('get_sent_friend_requests', { player_id: playerId }),
  ])
  const pick = (r) => (r.status === 'fulfilled' ? (r.value?.data || []) : [])
  return {
    friends: pick(friendsRes),
    pending: pick(pendingRes),
    suggestions: pick(suggestRes),
    sent: pick(sentRes),
    friendsFailed: friendsRes.status === 'rejected',
    pendingFailed: pendingRes.status === 'rejected',
    suggestionsFailed: suggestRes.status === 'rejected',
    sentFailed: sentRes.status === 'rejected',
  }
}
